import { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';
import AddCardModalWrapper from 'views/Accounts/components/Checkout.Com/AddCardModal/Wrapper';
import { OpenSlideoutFnType, SlideOutPanelViews, TopupSlideoutViewState } from 'views/Accounts/types';
import SlideOutPanel from 'components/SlideOutPanel';
import ImpersonationBanner from 'components/ImpersonationBanner';
import { TopupSlideout, TopupSlideoutRefType } from 'views/shared/TopupSlideout';
import { useTranslation } from 'react-i18next';
import { NavigationPath } from 'types/DomainTypes';
import { AppEventTypes } from 'types/AppType';
import { AppContext } from 'context/ContextProvider';
import { useSyncHashToRouter } from 'hooks/useSyncHashToRouter';
import { ProtectedRouteContextType } from './useProtectedPageContext';

export const ProtectedPageContainer: FC = () => {
  const { t } = useTranslation();
  const topUpSlideoutRef = useRef();
  const [slideOutPanelConfig, setSlideOutPanelConfig] = useState({ ...defaultSlideouState });
  const { cardManagement14583 } = useFlags();

  const { pathname, hash } = useLocation();
  const { dispatch } = useContext(AppContext);

  // Mirror direct `window.location.hash` changes into the router so the hash-driven success/failure
  // modals react on a frictionless (non-3DS) Flow completion (which only changes the hash, no reload).
  useSyncHashToRouter();

  const openSlideout: OpenSlideoutFnType = useCallback(
    (view, config) => {
      setSlideOutPanelConfig({
        showBackButton: false,
        open: true,
        title: t('account:slideout.topup.title'),
        view,
        timestamp: Date.now(),
        initialModel: undefined,
        initialView: undefined,
        customTemplate: () => null,
        ...(config || {}),
      });
    },
    [t],
  );

  useEffect(() => {
    if (pathname.endsWith(NavigationPath.PAYMENT_CONFIRMATION)) {
      dispatch({
        type: AppEventTypes.UPDATE_STATE,
        payload: {
          isConfirmPayment: true,
        },
      });
      openSlideout(SlideOutPanelViews.TOP_UP);
    }
  }, [dispatch, openSlideout, pathname]);

  // A frictionless (no-3DS) top-up completes by setting the `#topUp_success` hash on the SAME page — no
  // redirect or reload — so the success modal (`AddCardModalWrapper`) renders on top while this slideout
  // stays open underneath. Dismiss it here so only the success modal shows. (The 3DS path full-reloads on
  // return, so the slideout is already gone there and this is a no-op.)
  useEffect(() => {
    if (hash === '#topUp_success') {
      setSlideOutPanelConfig((prev) => (prev.open ? { ...prev, open: false } : prev));
    }
  }, [hash]);

  const onBack = () => {
    (topUpSlideoutRef?.current as unknown as TopupSlideoutRefType)?.onBack();
  };

  const onClose = () => {
    setSlideOutPanelConfig((prev) => ({ ...prev, open: false }));
  };

  const onTopupSlideoutStateChange = (state: TopupSlideoutViewState) => {
    setSlideOutPanelConfig((prev) => ({ ...prev, showBackButton: state.showBackButton }));
  };

  const context: ProtectedRouteContextType = { openSlideout, backSlideout: onBack, closeSlidout: onClose };

  return (
    <>
      {/* Code that needs to be rendered on all protected routes should be placed here. */}
      <ImpersonationBanner />
      <SlideOutPanel
        showBackButton={slideOutPanelConfig.showBackButton}
        headClassName="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 text-gray-800 dark:text-white"
        title={slideOutPanelConfig.title}
        isOpen={slideOutPanelConfig.open}
        onBack={onBack}
        onClose={() => setSlideOutPanelConfig({ ...slideOutPanelConfig, open: false })}
      >
        {slideOutPanelConfig.open && slideOutPanelConfig.view === SlideOutPanelViews.TOP_UP && (
          <TopupSlideout
            initialModel={slideOutPanelConfig.initialModel}
            initialView={slideOutPanelConfig.initialView}
            ref={topUpSlideoutRef}
            onClose={onClose}
            onStateChange={onTopupSlideoutStateChange}
          />
        )}
        {slideOutPanelConfig.open &&
          slideOutPanelConfig.view === SlideOutPanelViews.CUSTOM &&
          slideOutPanelConfig.customTemplate &&
          slideOutPanelConfig.customTemplate()}
      </SlideOutPanel>
      {cardManagement14583 && <AddCardModalWrapper />}
      <Outlet context={context} />
    </>
  );
};

const defaultSlideouState = {
  showBackButton: false,
  open: false,
  view: SlideOutPanelViews.TOP_UP,
  title: '',
  timestamp: Date.now(),
  initialModel: undefined,
  initialView: undefined,
  customTemplate: () => null,
};
